// client/src/components/pages/LexiStar/Commissioning/CommissioningRun.jsx
//
// The commissioning workspace for one parallel run (LS-09 §5.20): a five-step
// stepper — (1) scope, (2) their records, (3) the daily comparison,
// (4) the accuracy report, (5) sign-off — with the current step in the URL
// (?step=). Steps are always reachable; each shows whether it is done. The
// comparison is (re)built with POST /parallel-runs/:id:match, idempotently.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaSyncAlt, FaCertificate, FaCheck } from 'react-icons/fa';
import {
  getParallelRun,
  getParallelRunReport,
  matchParallelRun,
  pickObject,
} from '../../../../services/lexistarAssurance';
import { ErrorBanner, NoticeBanner, LoadingState, cardClasses } from '../StatusBoard/atoms';
import { fmtDateTime, fmtNum } from '../StatusBoard/format';
import { buttonClasses } from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  STEPS,
  METRICS,
  floorText,
  dailySeries,
  recordCounts,
  reachedStep,
  RUN_STATE_META,
} from './commissioningLogic';
import RecordImport from './RecordImport';
import AccuracyReport, { DailyComparison } from './AccuracyReport';
import SignOffPanel from './SignOffPanel';

function Stepper({ current, reached, onGo }) {
  return (
    <nav aria-label="Commissioning steps" className="overflow-x-auto">
      <ol className="grid grid-cols-5 min-w-[600px] gap-1 sm:gap-2">
        {STEPS.map((s, i) => {
          const done = i < reached || (i === 4 && reached >= 4);
          const active = i === current;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onGo(s.id)}
                aria-current={active ? 'step' : undefined}
                className={`flex items-center gap-2 w-full h-full px-2 sm:px-3 py-2 min-h-[48px] rounded-lg text-left border ${active ? 'border-slate-900 bg-surface' : 'border-transparent hover:bg-slate-100'}`}
              >
                <span
                  className={`flex flex-none items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${done ? 'bg-slate-900 text-white' : active ? 'ring-2 ring-slate-900 text-slate-900' : 'ring-1 ring-slate-300 text-slate-500'}`}
                  aria-hidden="true"
                >
                  {done ? <FaCheck size={9} /> : i + 1}
                </span>
                <span>
                  <span
                    className={`block text-sm font-semibold ${active ? 'text-slate-900' : 'text-slate-700'}`}
                  >
                    {s.label}
                  </span>
                  <span className="hidden xl:block text-[11px] leading-snug text-slate-500">
                    {s.help}
                  </span>
                </span>
                <span className="sr-only">{done ? '(done)' : ''}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function CommissioningRun({ id, lanesById = {} }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [run, setRun] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [matching, setMatching] = useState(false);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      try {
        if (!quiet) setLoading(true);
        const res = await getParallelRun(id);
        const r = pickObject(res, 'parallel_run', 'run');
        setRun(r);
        if (r?.report) setReport(r.report);
        else if (r?.report_at) {
          getParallelRunReport(id)
            .then((rep) => setReport(rep?.report || rep))
            .catch(() => {});
        }
        setError(null);
      } catch (err) {
        if (!quiet)
          setError(
            err.response?.status === 404
              ? 'Parallel run not found, or not visible to your organisation.'
              : errorMessage(err, 'Failed to load the parallel run.')
          );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    load();
  }, [load]);

  const reached = reachedStep(run, report);
  const stepId = STEPS.some((s) => s.id === searchParams.get('step'))
    ? searchParams.get('step')
    : STEPS[Math.min(reached, 4)].id;
  const current = STEPS.findIndex((s) => s.id === stepId);
  const go = (sid) => setSearchParams({ step: sid }, { replace: true });

  const match = async () => {
    setMatching(true);
    setError(null);
    try {
      await matchParallelRun(id);
      const rep = await getParallelRunReport(id);
      setReport(rep?.report || rep);
      setNotice('Comparison rebuilt from the latest records.');
      await load({ quiet: true });
    } catch (err) {
      setError(errorMessage(err, 'The comparison could not be run.'));
    } finally {
      setMatching(false);
    }
  };

  const counts = useMemo(() => recordCounts(run), [run]);
  const series = useMemo(() => dailySeries(run), [run]);
  const signedOff = run?.state === 'signed_off' || !!run?.certificate_id;

  if (loading && !run) return <LoadingState label="Loading the parallel run..." />;
  if (!run)
    return (
      <>
        <ErrorBanner message={error} />
        <Link to="/lexistar/commissioning" className={buttonClasses.secondary}>
          <FaArrowLeft size={11} /> All parallel runs
        </Link>
      </>
    );

  const floors = run.floors || {};
  const matchButton = (
    <button
      type="button"
      onClick={match}
      disabled={matching || signedOff}
      className={buttonClasses.primary}
    >
      <FaSyncAlt size={11} className={matching ? 'animate-spin' : ''} />{' '}
      {matching ? 'Comparing…' : 'Run the comparison'}
    </button>
  );

  return (
    <div className="space-y-4">
      <ErrorBanner message={error} onDismiss={() => setError(null)} />
      <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/lexistar/commissioning"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <FaArrowLeft size={10} /> All parallel runs
        </Link>
        {signedOff && (
          <Link
            to={`/lexistar/commissioning/${run.id}/certificate`}
            className={buttonClasses.secondary}
          >
            <FaCertificate size={11} /> Certificate
          </Link>
        )}
      </div>

      <section className={`${cardClasses} p-4 sm:p-5`} aria-label="Parallel run">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 className="text-lg font-bold text-slate-900">{run.name}</h2>
          <span className="text-xs font-semibold text-slate-600">
            {RUN_STATE_META[run.state]?.glyph} {RUN_STATE_META[run.state]?.label || run.state}
          </span>
        </div>
        <p className="text-sm text-slate-600 tabular-nums">
          {fmtDateTime(run.window_from)} → {fmtDateTime(run.window_to)} ·{' '}
          {fmtNum((run.asset_keys || []).length)} truck
          {(run.asset_keys || []).length === 1 ? '' : 's'} ·{' '}
          {(run.lane_ids || []).length
            ? `${fmtNum(run.lane_ids.length)} lane${run.lane_ids.length === 1 ? '' : 's'}`
            : 'all lanes'}
        </p>
        <div className="mt-4">
          <Stepper current={current} reached={reached} onGo={go} />
        </div>
      </section>

      {stepId === 'scope' && (
        <section className={`${cardClasses} p-4 sm:p-5 space-y-3`} aria-label="Scope">
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500">Window</dt>
              <dd className="text-slate-900 tabular-nums">
                {fmtDateTime(run.window_from)} → {fmtDateTime(run.window_to)}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500">Trucks</dt>
              <dd className="text-slate-900 font-mono text-xs break-words">
                {(run.asset_keys || []).map((k) => String(k).replace(/^reg:/, '')).join(', ') ||
                  'All trucks'}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500">Lanes</dt>
              <dd className="text-slate-900">
                {(run.lane_ids || []).map((l) => lanesById[l] || l).join(', ') || 'All lanes'}
              </dd>
            </div>
          </dl>
          <div>
            <h3 className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Floors</h3>
            <ul className="flex flex-wrap gap-2 text-sm">
              {METRICS.map((m) => (
                <li
                  key={m.key}
                  className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-800 tabular-nums"
                >
                  {m.label} {floorText(m, floors[m.key])}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => go('records')} className={buttonClasses.primary}>
              Next: their records
            </button>
          </div>
        </section>
      )}

      {stepId === 'records' && (
        <RecordImport
          runId={run.id}
          counts={counts}
          disabled={signedOff}
          onImported={() => load({ quiet: true })}
        />
      )}

      {stepId === 'daily' && (
        <section className={`${cardClasses} p-4 sm:p-5 space-y-3`} aria-label="Daily comparison">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-600">
              Their trip sheets against the trips LexiStar detected, day by day.
              {run.report_at && <> Last compared {fmtDateTime(run.report_at)}.</>}
            </p>
            {matchButton}
          </div>
          <DailyComparison series={series} />
        </section>
      )}

      {stepId === 'report' && (
        <div className="space-y-3">
          <div className="flex justify-end">{matchButton}</div>
          <AccuracyReport report={report} floors={floors} lanesById={lanesById} />
        </div>
      )}

      {stepId === 'signoff' && (
        <SignOffPanel
          run={run}
          report={report}
          onSignedOff={async () => {
            setNotice('Signed off. The certificate is issued.');
            await load({ quiet: true });
          }}
        />
      )}
    </div>
  );
}
